import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtEUR, L } from "@/constants/labels";
import { CheckCircle2, AlertTriangle, XCircle, MinusCircle } from "lucide-react";
import ProfileCompletionCard from "@/components/ProfileCompletionCard";

const ConformiteBadge = ({ status }) => {
  if (status === "valide") return <span className="badge-valid" data-testid="conf-valide"><CheckCircle2 size={12} /> {L.compliance.valide}</span>;
  if (status === "expirant") return <span className="badge-warn"><AlertTriangle size={12} /> {L.compliance.expirant}</span>;
  if (status === "expire") return <span className="badge-danger"><XCircle size={12} /> {L.compliance.expire}</span>;
  return <span className="badge-muted"><MinusCircle size={12} /> {L.compliance.incomplet}</span>;
};

function EntrepriseDash({ user }) {
  const [stats, setStats] = useState({ besoins: 0, missions: 0, pending: 0 });
  useEffect(() => {
    (async () => {
      const [b, m, p] = await Promise.all([
        api.get("/besoins", { params: { mine: true } }),
        api.get("/missions"),
        api.get("/avis/pending").catch(() => ({ data: [] })),
      ]);
      setStats({ besoins: b.data.length, missions: m.data.length, pending: p.data.length });
    })();
  }, []);
  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">Bonjour, {user.raison_sociale}</h1>
      <p className="text-sm text-[#3E4C59] mt-1">{L.wording.espaceDonneurOrdre}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">{L.wording.dashMesChantiers}</div><div className="text-3xl font-bold text-[#1B3A5C]" data-testid="stat-besoins">{stats.besoins}</div></div>
        <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">Mes missions</div><div className="text-3xl font-bold text-[#1B3A5C]" data-testid="stat-missions">{stats.missions}</div></div>
        <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">À noter</div><div className="text-3xl font-bold text-[#E8710A]" data-testid="stat-avis-pending">{stats.pending}</div><div className="text-xs text-[#3E4C59]">mission(s) terminée(s)</div></div>
        <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">Abonnement</div><div className="text-lg font-semibold text-[#1F2933]">Freemium</div></div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        <Link to="/app/prestataires" className="btn-primary" data-testid="cta-search">{L.wording.dashRechercherSousTraitant}</Link>
        <Link to="/app/chantiers/nouveau" className="btn-accent" data-testid="cta-nouveau-besoin">{L.wording.dashPublierChantier}</Link>
        {stats.pending > 0 && (
          <Link to="/app/missions" className="btn-outline" data-testid="cta-noter-missions">
            Noter mes missions terminées ({stats.pending})
          </Link>
        )}
      </div>
    </div>
  );
}

function PrestataireDash({ user }) {
  const [profile, setProfile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [avis, setAvis] = useState([]);
  const [pending, setPending] = useState(0);
  useEffect(() => {
    (async () => {
      const [p, d, a, pn] = await Promise.all([
        api.get("/prestataires/me/profile").catch(() => ({ data: {} })),
        api.get("/documents/me").catch(() => ({ data: [] })),
        api.get("/avis/me").catch(() => ({ data: [] })),
        api.get("/avis/pending").catch(() => ({ data: [] })),
      ]);
      setProfile(p.data); setDocs(d.data); setAvis(a.data); setPending(pn.data.length);
    })();
  }, []);
  const docCount = docs.length;
  const noteMoy = avis.length ? (avis.reduce((s, a) => s + a.note, 0) / avis.length).toFixed(1) : "—";
  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">Bonjour, {user.raison_sociale}</h1>
      <p className="text-sm text-[#3E4C59] mt-1">{L.wording.espaceArtisanIndep}</p>

      <div className="mt-6">
        <ProfileCompletionCard />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">Profil</div><div className="text-lg font-semibold text-[#1F2933]">{profile?.metier || "À compléter"}</div></div>
        <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">Documents déposés</div><div className="text-3xl font-bold text-[#1B3A5C]" data-testid="stat-docs">{docCount}/5</div></div>
        <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">Note moyenne</div><div className="text-3xl font-bold text-[#1B3A5C]">{noteMoy}</div><div className="text-xs text-[#3E4C59]">{avis.length} avis</div></div>
        <div className="card p-4"><div className="text-xs uppercase text-[#3E4C59]">À noter</div><div className="text-3xl font-bold text-[#E8710A]" data-testid="stat-avis-pending">{pending}</div><div className="text-xs text-[#3E4C59]">mission(s) terminée(s)</div></div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        <Link to="/app/mon-profil" className="btn-primary" data-testid="cta-profil">Compléter mon profil</Link>
        <Link to="/app/mes-documents" className="btn-outline" data-testid="cta-docs">Mes documents</Link>
        <Link to="/app/chantiers" className="btn-accent" data-testid="cta-besoins">{L.wording.dashVoirChantiersOuverts}</Link>
        {pending > 0 && (
          <Link to="/app/missions" className="btn-outline" data-testid="cta-noter-missions">
            Noter mes missions terminées ({pending})
          </Link>
        )}
        <button
          onClick={() => {
            const url = `${window.location.origin}/inscription?role=prestataire`;
            navigator.clipboard.writeText(url).then(
              () => toast.success("Lien d'invitation copié — partagez-le à un confrère"),
              () => toast.error("Copie impossible")
            );
          }}
          className="btn-outline"
          data-testid="cta-invite-confrere"
        >Inviter un confrère</button>
      </div>
    </div>
  );
}

export default function DashboardRouter() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "entreprise") return <EntrepriseDash user={user} />;
  if (user.role === "prestataire") return <PrestataireDash user={user} />;
  return <div className="p-4">Redirection…</div>;
}
